import { Link, Navigate } from "react-router-dom";
import { useCustomer } from "../lib/CustomerContext";
import { formatShopifyPrice } from "../lib/shopify";
import { motion } from "framer-motion";
import { LogOut, Package, ExternalLink, User, Sparkles } from "lucide-react";
import { usePirate, formatBerry } from "../lib/PirateContext";

const STATUS_STYLE = {
    PAID: "bg-bounty text-parchment",
    PENDING: "bg-gold text-ink",
    REFUNDED: "bg-ink text-parchment",
    FULFILLED: "bg-bounty text-parchment",
    UNFULFILLED: "bg-gold text-ink",
};

export default function CustomerAccount() {
    const { customer, isAuthed, logout, loading } = useCustomer();
    const { level, berry, xp } = usePirate();

    if (!isAuthed && !loading) {
        return <Navigate to="/account/login" replace />;
    }

    if (loading || !customer) {
        return (
            <div className="max-w-3xl mx-auto px-5 py-20 text-center">
                <div className="font-hand text-3xl text-bounty animate-pulse">
                    caricamento diario di bordo...
                </div>
            </div>
        );
    }

    const orders = (customer.orders?.edges || []).map((e) => e.node);

    return (
        <div className="max-w-4xl mx-auto px-5 lg:px-10 py-12" data-testid="customer-account-page">
            <div className="flex items-start justify-between flex-wrap gap-4 border-b-2 border-ink pb-6 mb-8">
                <div>
                    <div className="font-hand text-2xl text-bounty">
                        il diario di bordo di
                    </div>
                    <h1 className="font-display text-5xl tracking-tight">
                        {customer.displayName?.toUpperCase() || "PIRATA"}
                    </h1>
                    <div className="font-mono text-xs text-ink/60 mt-1">
                        {customer.email}
                    </div>
                </div>
                <button
                    onClick={logout}
                    className="btn-manga"
                    data-testid="customer-logout-btn"
                >
                    <LogOut size={14} className="mr-2" />
                    Esci
                </button>
            </div>

            {/* Pirate stats card */}
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
                <div className="bounty-card p-4 flex items-center gap-3">
                    <div className="w-12 h-12 bg-bounty text-parchment border-2 border-ink flex items-center justify-center font-display text-2xl">
                        {level.level}
                    </div>
                    <div>
                        <div className="font-hand text-xs text-ink/60">livello</div>
                        <div className="font-display text-lg tracking-tight">
                            {level.name.toUpperCase()}
                        </div>
                    </div>
                </div>
                <div className="bounty-card p-4 bg-gold flex items-center gap-3">
                    <Sparkles size={22} />
                    <div>
                        <div className="font-hand text-xs">tesoro</div>
                        <div className="font-mono text-lg font-bold">
                            {formatBerry(berry)}
                        </div>
                    </div>
                </div>
                <div className="bounty-card p-4 flex items-center gap-3">
                    <User size={22} className="text-bounty" />
                    <div>
                        <div className="font-hand text-xs text-ink/60">
                            ordini totali
                        </div>
                        <div className="font-display text-2xl">
                            {customer.numberOfOrders || 0}
                        </div>
                    </div>
                </div>
            </div>

            {/* Orders */}
            <div className="flex items-end justify-between mb-6">
                <h2 className="font-display text-4xl tracking-tight">
                    I MIEI ORDINI
                </h2>
                <div className="font-mono text-xs text-ink/60">
                    XP totale: {xp}
                </div>
            </div>

            {orders.length === 0 ? (
                <div className="bounty-card p-10 text-center">
                    <Package size={40} className="mx-auto text-bounty mb-3" />
                    <div className="font-hand text-2xl text-bounty">
                        nessun tesoro ancora conquistato
                    </div>
                    <p className="text-sm text-ink/70 mt-2">
                        Il primo ordine ti farà guadagnare 500 XP e 1000 Berry!
                    </p>
                    <Link
                        to="/figure"
                        className="btn-manga btn-manga-red mt-5 inline-flex"
                    >
                        Esplora le figure
                    </Link>
                </div>
            ) : (
                <div className="space-y-4" data-testid="customer-orders-list">
                    {orders.map((o, i) => (
                        <motion.div
                            key={o.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="bounty-card p-5"
                            data-testid={`customer-order-${o.orderNumber}`}
                        >
                            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                                <div>
                                    <div className="font-hand text-xl text-bounty">
                                        ordine
                                    </div>
                                    <div className="font-display text-2xl tracking-tight">
                                        {o.name}
                                    </div>
                                    <div className="font-mono text-xs text-ink/60 mt-1">
                                        {new Date(o.processedAt).toLocaleDateString(
                                            "it-IT",
                                            {
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            }
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-2xl text-bounty font-bold">
                                        {formatShopifyPrice(
                                            o.totalPrice.amount,
                                            o.totalPrice.currencyCode
                                        )}
                                    </div>
                                    <div className="flex gap-2 justify-end mt-1 flex-wrap">
                                        {o.financialStatus && (
                                            <span
                                                className={`px-2 py-0.5 border-2 border-ink text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[o.financialStatus] || "bg-white"}`}
                                            >
                                                {o.financialStatus}
                                            </span>
                                        )}
                                        {o.fulfillmentStatus && (
                                            <span
                                                className={`px-2 py-0.5 border-2 border-ink text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[o.fulfillmentStatus] || "bg-white"}`}
                                            >
                                                {o.fulfillmentStatus}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid gap-2 pt-3 border-t border-ink/10">
                                {(o.lineItems?.edges || []).map((le, j) => {
                                    const li = le.node;
                                    return (
                                        <div
                                            key={j}
                                            className="flex items-center gap-3 text-sm"
                                        >
                                            {li.variant?.image?.url && (
                                                <img
                                                    src={li.variant.image.url}
                                                    alt=""
                                                    className="w-10 h-10 object-cover border border-ink"
                                                />
                                            )}
                                            <span className="flex-1">
                                                {li.title}
                                            </span>
                                            <span className="font-mono text-xs text-ink/60">
                                                × {li.quantity}
                                            </span>
                                            {li.variant?.price && (
                                                <span className="font-mono text-sm">
                                                    {formatShopifyPrice(
                                                        li.variant.price.amount,
                                                        li.variant.price
                                                            .currencyCode
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {o.statusUrl && (
                                <a
                                    href={o.statusUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-manga btn-manga-ghost mt-4 text-sm"
                                    data-testid={`order-track-${o.orderNumber}`}
                                >
                                    Traccia spedizione
                                    <ExternalLink size={12} className="ml-2" />
                                </a>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
